import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "src/redux/hooks";

interface Props {
  id: string;
  dataTag: string | undefined;
  onChangeInput?: (v: any) => void;
  value: string;
}
/**
 * Custom hook to handle previous data retrieval and updates for ambassador inputs.
 *
 * @param props - An object containing the following properties:
 *   - id: A string representing the unique identifier for the input field.
 *   - dataTag: An optional string representing the tag used to fetch previous data.
 *   - onChangeInput: An optional callback function to handle input value changes.
 *   - value: A string representing the current value of the input field.
 */
const useAmbPrevData = ({ id, dataTag, onChangeInput, value }: Props) => {
  const { setValue } = useFormContext();
  const { features } = useSelector(state => state.generatorSlice);
  useEffect(() => {
    if (!dataTag) return;
    const newValue = features?.ambGetPreviousData?.[dataTag];

    // Handle different data types and units
    let processedValue = newValue;
    if (typeof newValue === "object" && newValue?.key) {
      processedValue = newValue.key;
    } // Handle GPS coordinates (lat, latNS, long, longEW) - these should use the full object
    const isGPSCoordinate = ["lat", "latNS", "long", "longEW"].includes(
      dataTag || "",
    );
    if (isGPSCoordinate && typeof newValue === "object") {
      processedValue = newValue;
    }

    // Handle security level - support both numeric and object formats
    const isSecurityLevel = dataTag === "seclevel";
    if (isSecurityLevel) {
      if (typeof newValue === "string" && ["1", "2", "3"].includes(newValue)) {
        // For numeric input, keep as is for typing
        processedValue = newValue;
      } else if (typeof newValue === "object" && newValue?.value) {
        // For object format, use the structured value
        processedValue = newValue;
      }
    }

    const parsedNewValue =
      typeof processedValue === "object"
        ? processedValue?.key || processedValue?.value
        : processedValue;

    if (parsedNewValue && parsedNewValue !== value) {
      if (onChangeInput) {
        // For GPS coordinates, pass the full object if available
        // For security level, pass the processed value (numeric or object)
        if (isGPSCoordinate) {
          onChangeInput(processedValue);
        } else if (isSecurityLevel) {
          onChangeInput(processedValue);
        } else {
          onChangeInput(parsedNewValue);
        }
      } else {
        // For form setValue, use the processed value
        if (isGPSCoordinate || isSecurityLevel) {
          setValue(id, processedValue);
        } else {
          setValue(id, newValue);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [features, dataTag, id, value]);
};
export default useAmbPrevData;
